/**
 * One customer in full, and everything the operator can do to them.
 *
 * Every action here posts to `admin_actions.py`, which writes a line in the customer's audit
 * trail, so the Activity tab shows what was done from this screen alongside what the customer
 * did themselves. There is no delete button. Closing is the whole of it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { download, request } from '../api/client';
import type {
  AccountStatus,
  ActionDone,
  Activity,
  CustomerDetail as Detail,
  InvitationIssued,
  StatusChanged,
  UpdateIdentityRequest,
} from '../api/types';
import { day, money, moment, monthName, num, since } from '../components/format';
import {
  Confirm,
  Failed,
  Loading,
  Metric,
  Modal,
  PageHead,
  Panel,
  Pill,
  Tabs,
  TokenReveal,
} from '../components/ui';
import { ConversationTable, useConversations } from './Conversations';

type Tab = 'overview' | 'people' | 'conversations' | 'activity' | 'billing' | 'settings';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'people', label: 'People & devices' },
  { key: 'conversations', label: 'Conversations' },
  { key: 'activity', label: 'Activity' },
  { key: 'billing', label: 'Billing' },
  { key: 'settings', label: 'Settings & actions' },
];

export function CustomerDetail() {
  const { organisationId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'overview';

  const { data, isPending, error } = useQuery({
    queryKey: ['customer', organisationId],
    queryFn: () => request<Detail>(`/api/v1/admin/customers/${organisationId}`),
  });

  if (isPending) return <Loading what="Opening the account" />;
  if (error) return <Failed error={error} fallback="Could not load this customer." />;

  const { identity } = data;
  return (
    <>
      <PageHead
        eyebrow={`Customer · ${identity.plan_code}`}
        title={identity.name}
        note={
          <>
            <Pill value={identity.account_status} /> · {identity.display_name},{' '}
            {identity.trading_description}. Created {day(identity.created_at)} · last active{' '}
            {since(data.last_active_at).toLowerCase()}.
          </>
        }
        actions={
          <Link className="button-quiet" to={`/conversations?organisation_id=${organisationId}`}>
            Read their conversations
          </Link>
        }
      />

      <Tabs tabs={TABS} current={tab} onChange={(next) => setParams({ tab: next })} />

      {tab === 'overview' ? <OverviewTab detail={data} /> : null}
      {tab === 'people' ? <PeopleTab detail={data} /> : null}
      {tab === 'conversations' ? <ConversationsTab organisationId={organisationId} /> : null}
      {tab === 'activity' ? <ActivityTab organisationId={organisationId} /> : null}
      {tab === 'billing' ? <BillingTab detail={data} /> : null}
      {tab === 'settings' ? <SettingsTab detail={data} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- overview

function OverviewTab({ detail }: { detail: Detail }) {
  const usage = detail.usage_30d;
  const turns = usage.chat_turns + usage.voice_turns + usage.night_shift_runs;
  return (
    <>
      <div className="cards">
        <Metric
          label={`Billed, ${monthName(detail.month)}`}
          value={money(detail.revenue_month_to_date_gbp)}
          note={
            detail.at_minimum
              ? `At the monthly minimum · heading for ${money(detail.revenue_projected_gbp)}`
              : `Heading for ${money(detail.revenue_projected_gbp)}`
          }
        />
        <Metric
          label="Cost to serve"
          value={money(detail.marginal_cost_gbp)}
          note="Marginal, this month"
        />
        <Metric
          label="Turns, 30 days"
          value={num(turns)}
          note={`${num(usage.voice_turns)} voice · ${num(usage.chat_turns)} text · ${num(usage.night_shift_runs)} night shift`}
        />
        <Metric
          label="Failed turns"
          value={num(usage.failed_turns)}
          note={`${num(usage.error_events)} server errors · ${num(usage.approvals_pending)} approvals waiting`}
          {...(usage.failed_turns ? { tone: 'bad' as const } : {})}
        />
      </div>

      <div className="panel-grid">
        <Panel eyebrow="Identity" title="Who they are">
          <dl className="kv">
            <dt>Slug</dt>
            <dd className="mono">{identityOf(detail).slug}</dd>
            <dt>Owner</dt>
            <dd>{identityOf(detail).owner_first_name ?? <span className="faint">not set</span>}</dd>
            <dt>Reply number</dt>
            <dd>{identityOf(detail).reply_number ?? <span className="faint">not set</span>}</dd>
            <dt>SMS sender</dt>
            <dd>
              {identityOf(detail).sms_sender_id ?? <span className="faint">not set</span>}{' '}
              {identityOf(detail).sms_sender_id ? (
                <Pill value={identityOf(detail).sms_sender_approved ? 'approved' : 'pending'} />
              ) : null}
            </dd>
            <dt>Payment ref</dt>
            <dd className="mono">{detail.payment_reference}</dd>
            <dt>Microsoft</dt>
            <dd className="mono">{identityOf(detail).microsoft_tenant_id}</dd>
            <dt>Features</dt>
            <dd>
              {Object.keys(identityOf(detail).feature_flags).length === 0 ? (
                <span className="faint">defaults</span>
              ) : (
                Object.entries(identityOf(detail).feature_flags).map(([key, on]) => (
                  <span key={key} style={{ marginRight: 8 }}>
                    <Pill value={on ? 'active' : 'closed'} /> {key}
                  </span>
                ))
              )}
            </dd>
          </dl>
        </Panel>

        <Panel eyebrow="This month" title="Cost by service" flush>
          {detail.cost_by_service.length === 0 ? (
            <div className="panel-body muted">Nothing priced yet this month.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="numeric">Quantity</th>
                  <th className="numeric">Cost</th>
                </tr>
              </thead>
              <tbody>
                {detail.cost_by_service.map((line) => (
                  <tr key={`${line.service}:${line.label}`}>
                    <td>
                      {line.label}
                      <div className="muted small">{line.service}</div>
                    </td>
                    <td className="numeric muted">
                      {num(line.quantity)} {line.unit}
                    </td>
                    <td className="numeric">{money(line.gbp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <Panel eyebrow="30 days" title="Usage">
        <dl className="kv">
          <dt>Conversations</dt>
          <dd>{num(usage.conversations)}</dd>
          <dt>Messages</dt>
          <dd>{num(usage.messages)}</dd>
          <dt>Tool calls</dt>
          <dd>{num(usage.tool_calls)}</dd>
          <dt>Voice heard</dt>
          <dd>{usage.voice_minutes} minutes</dd>
        </dl>
      </Panel>
    </>
  );
}

function identityOf(detail: Detail) {
  return detail.identity;
}

// ---------------------------------------------------------------- people

function PeopleTab({ detail }: { detail: Detail }) {
  const client = useQueryClient();
  const organisationId = detail.identity.organisation_id;
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<InvitationIssued | null>(null);
  const [revoking, setRevoking] = useState<{
    userId: string;
    deviceId: string;
    name: string;
  } | null>(null);

  const revoke = useMutation({
    mutationFn: ({ userId, deviceId }: { userId: string; deviceId: string }) =>
      request<ActionDone>(
        `/api/v1/admin/customers/${organisationId}/devices/${userId}/${deviceId}/revoke`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      setRevoking(null);
      void client.invalidateQueries({ queryKey: ['customer', organisationId] });
    },
  });

  return (
    <>
      <Panel
        eyebrow="Members"
        title={`${detail.members.length} ${detail.members.length === 1 ? 'person' : 'people'}`}
        actions={
          <button type="button" className="button-quiet" onClick={() => setInviting(true)}>
            Invite somebody
          </button>
        }
        flush
      >
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Last seen</th>
              <th className="numeric">Devices</th>
              <th className="numeric">Conversations</th>
            </tr>
          </thead>
          <tbody>
            {detail.members.map((member) => (
              <tr key={member.user_id}>
                <td>
                  {member.full_name || <span className="faint">no name</span>}
                  <div className="muted small">{member.email}</div>
                </td>
                <td>
                  <Pill value={member.role} />
                </td>
                <td className="muted">{day(member.joined_at)}</td>
                <td>{since(member.last_seen_at)}</td>
                <td className="numeric">{member.active_devices}</td>
                <td className="numeric">
                  <Link
                    to={`/conversations?organisation_id=${organisationId}&user_id=${member.user_id}`}
                  >
                    {member.conversations}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {detail.invitations.length ? (
        <Panel eyebrow="Invitations" title="Outstanding and past" flush>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>State</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Accepted</th>
              </tr>
            </thead>
            <tbody>
              {detail.invitations.map((invitation) => (
                <tr key={invitation.invitation_id}>
                  <td>{invitation.email}</td>
                  <td>
                    <Pill value={invitation.role} />
                  </td>
                  <td>
                    <Pill value={invitation.state} />
                  </td>
                  <td className="muted">{moment(invitation.created_at)}</td>
                  <td className="muted">{moment(invitation.expires_at)}</td>
                  <td className="muted">{moment(invitation.accepted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <Panel eyebrow="Devices" title="Phones and browsers signed in" flush>
        {detail.devices.length === 0 ? (
          <div className="panel-body muted">Nobody has signed in on a device yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Device</th>
                <th>Who</th>
                <th>Platform</th>
                <th>Build</th>
                <th>Last seen</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detail.devices.map((device) => (
                <tr key={device.device_session_id}>
                  <td>{device.device_name}</td>
                  <td className="muted">{device.email}</td>
                  <td className="muted">{device.platform}</td>
                  <td className="muted mono">{device.client_build ?? '—'}</td>
                  <td>{since(device.last_seen_at)}</td>
                  <td>
                    <Pill value={device.revoked_at ? 'revoked' : 'active'} />
                  </td>
                  <td className="numeric">
                    {device.revoked_at ? null : (
                      <button
                        type="button"
                        className="button-quiet danger"
                        onClick={() =>
                          setRevoking({
                            userId: device.user_id,
                            deviceId: device.device_id,
                            name: `${device.device_name} (${device.email})`,
                          })
                        }
                      >
                        Sign out
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {inviting ? (
        <InviteMember
          organisationId={organisationId}
          onIssued={(result) => {
            setInviting(false);
            setIssued(result);
            void client.invalidateQueries({ queryKey: ['customer', organisationId] });
          }}
          onClose={() => setInviting(false)}
        />
      ) : null}
      {issued ? (
        <TokenReveal
          email={issued.email}
          organisation={issued.organisation_name}
          token={issued.token}
          expires={day(issued.expires_at)}
          onClose={() => setIssued(null)}
        />
      ) : null}
      {revoking ? (
        <Confirm
          title="Sign this device out?"
          body={
            <>
              <p style={{ margin: 0 }}>
                <strong>{revoking.name}</strong> will be refused on its next request and will have
                to sign in again. Nothing else changes.
              </p>
              {revoke.error ? (
                <Failed error={revoke.error} fallback="Could not sign the device out." />
              ) : null}
            </>
          }
          verb="Sign out"
          busy={revoke.isPending}
          onConfirm={() => revoke.mutate({ userId: revoking.userId, deviceId: revoking.deviceId })}
          onClose={() => setRevoking(null)}
        />
      ) : null}
    </>
  );
}

function InviteMember({
  organisationId,
  onIssued,
  onClose,
}: {
  organisationId: string;
  onIssued: (issued: InvitationIssued) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'owner'>('member');
  const [validDays, setValidDays] = useState(7);
  const invite = useMutation({
    mutationFn: () =>
      request<InvitationIssued>(`/api/v1/admin/customers/${organisationId}/invitations`, {
        method: 'POST',
        body: { email: email.trim(), role, valid_days: validDays },
      }),
    onSuccess: onIssued,
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    invite.mutate();
  }
  return (
    <Modal title="Invite somebody" onClose={onClose}>
      <form onSubmit={submit}>
        {invite.error ? (
          <Failed error={invite.error} fallback="Could not issue the invitation." />
        ) : null}
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </label>
        <div className="field-grid">
          <label className="field">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value as 'member' | 'owner')}>
              <option value="member">Member</option>
              <option value="owner">Owner</option>
            </select>
          </label>
          <label className="field">
            <span>Valid for (days)</span>
            <input
              type="number"
              min={1}
              max={30}
              value={validDays}
              onChange={(e) => setValidDays(Number(e.target.value))}
            />
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="button-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={invite.isPending}>
            {invite.isPending ? 'Issuing…' : 'Issue invitation'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- conversations

function ConversationsTab({ organisationId }: { organisationId: string }) {
  const [search, setSearch] = useState('');
  const query = useConversations({ organisationId, search });
  return (
    <>
      <div className="toolbar">
        <input
          type="search"
          placeholder="Filter by title or person…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ minWidth: 280 }}
          aria-label="Filter conversations"
        />
        <span className="spacer" />
        <Link className="small" to={`/conversations?organisation_id=${organisationId}`}>
          Open in the browser →
        </Link>
      </div>
      {query.error ? <Failed error={query.error} fallback="Could not load conversations." /> : null}
      <Panel flush>
        {query.isPending || !query.data ? (
          <div className="panel-body muted">Loading…</div>
        ) : (
          <ConversationTable rows={query.data.conversations} hideOrganisation />
        )}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- activity

function ActivityTab({ organisationId }: { organisationId: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['activity', organisationId],
    queryFn: () =>
      request<Activity>(`/api/v1/admin/customers/${organisationId}/activity?limit=200`),
  });
  if (isPending) return <Loading />;
  if (error) return <Failed error={error} fallback="Could not load activity." />;
  return (
    <Panel eyebrow="Timeline" title="Everything that happened, newest first" flush>
      {data.items.length === 0 ? (
        <div className="panel-body muted">Nothing recorded yet.</div>
      ) : (
        <ul className="timeline">
          {data.items.map((item, index) => (
            <li key={`${item.at}:${index}`}>
              <span className="when">{moment(item.at)}</span>
              <span className={`kind ${item.kind}`}>{item.kind}</span>
              <div>
                <div className="row" style={{ gap: 8 }}>
                  {item.conversation_id ? (
                    <Link to={`/conversations/${item.conversation_id}`}>{item.title}</Link>
                  ) : (
                    <span>{item.title}</span>
                  )}
                  {item.status ? <Pill value={item.status} /> : null}
                  {item.actor_email ? (
                    <span className="muted small">{item.actor_email}</span>
                  ) : null}
                </div>
                {item.detail ? <div className="detail">{item.detail}</div> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------- billing

function InvoiceLink({ organisationId, month }: { organisationId: string; month: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'failed'>('idle');
  const period = month.slice(0, 7);
  const fetchIt = () => {
    setState('busy');
    download(
      `/api/v1/admin/customers/${organisationId}/invoice/${period}`,
      `invoice-${period}.pdf`,
    ).then(
      () => setState('idle'),
      // The filename the server sends is the invoice number; this one is only what the browser
      // falls back to, and it never gets used unless the Content-Disposition header goes missing.
      () => setState('failed'),
    );
  };
  if (state === 'failed') return <span className="warn">not available</span>;
  return (
    <button type="button" className="linklike" disabled={state === 'busy'} onClick={fetchIt}>
      {state === 'busy' ? 'fetching' : 'invoice'}
    </button>
  );
}

function BillingTab({ detail }: { detail: Detail }) {
  const organisationId = detail.identity.organisation_id;
  return (
    <>
      <div className="cards">
        <Metric
          label={`Showing, ${monthName(detail.month)}`}
          value={money(detail.revenue_month_to_date_gbp)}
          note={detail.at_minimum ? 'Sitting at the monthly minimum' : 'Above the minimum'}
        />
        <Metric
          label="Projected"
          value={money(detail.revenue_projected_gbp)}
          note="At the rate the month is running"
        />
        <Metric
          label="Owed from last month"
          value={money(detail.due_amount_gbp, 'Nothing')}
          note={
            detail.due_month
              ? `${monthName(detail.due_month)} · ${
                  detail.due_locked ? 'app locked until paid' : (detail.due_state ?? '')
                }`
              : 'Settled'
          }
          {...(detail.due_amount_gbp ? { tone: detail.due_locked ? 'bad' : 'warn' } : {})}
        />
        <Metric
          label="On account"
          value={
            Number(detail.credit_balance_gbp) > 0 ? money(detail.credit_balance_gbp) : 'Nothing'
          }
          note="Paid up front. Comes off the next bill before he is asked for anything."
        />
        <Metric
          label="Payment reference"
          value={<span className="mono">{detail.payment_reference}</span>}
          small
          note={
            detail.due_pay_url ? (
              <>
                The pay button on his phone opens{' '}
                <a href={detail.due_pay_url} target="_blank" rel="noreferrer">
                  this Stripe link
                </a>
                . Bank transfer still works with the reference.
              </>
            ) : (
              'Bank transfer, or the pay button in the app once a bill is raised.'
            )
          }
        />
      </div>

      <Panel eyebrow="Bills" title="Every month on record" flush>
        {detail.bills.length === 0 ? (
          <div className="panel-body muted">No bill has been raised for this customer yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th className="numeric">Amount</th>
                <th className="numeric">From credit</th>
                <th>Sent</th>
                <th>Confirmed</th>
                <th>Via</th>
                <th>Stripe</th>
                <th>Invoice</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {detail.bills.map((bill) => (
                <tr key={bill.month}>
                  <td>{monthName(bill.month)}</td>
                  <td className="numeric">{money(bill.amount_gbp)}</td>
                  <td className="numeric muted">
                    {Number(bill.credit_applied_gbp) > 0 ? money(bill.credit_applied_gbp) : '—'}
                  </td>
                  <td className="muted">{moment(bill.marked_sent_at)}</td>
                  <td className={bill.confirmed_at ? 'good' : 'warn'}>
                    {bill.confirmed_at ? moment(bill.confirmed_at) : 'Unpaid'}
                  </td>
                  <td className="muted">{bill.confirmed_via ?? '—'}</td>
                  <td className="muted">
                    {bill.pay_url ? (
                      <a href={bill.pay_url} target="_blank" rel="noreferrer">
                        {bill.confirmed_via === 'stripe' ? 'paid here' : 'pay link'}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="muted">
                    <InvoiceLink month={bill.month} organisationId={organisationId} />
                  </td>
                  <td className="muted wrap">{bill.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {detail.credits.length > 0 ? (
        <Panel eyebrow="Top-ups" title="Money paid before there was a bill for it" flush>
          <table>
            <thead>
              <tr>
                <th>Paid</th>
                <th className="numeric">Amount</th>
                <th>How</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {detail.credits.map((credit) => (
                <tr key={`${credit.paid_at}-${credit.amount_gbp}`}>
                  <td className="muted">{moment(credit.paid_at)}</td>
                  <td className="numeric">{money(credit.amount_gbp)}</td>
                  <td className="muted">{credit.source}</td>
                  <td className="muted wrap">{credit.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <p className="muted small">
        A payment made through the app confirms itself: Stripe tells the server and the row above
        reads “stripe”. A bank transfer is still confirmed from the terminal — see the runbooks on
        the Company page. A month met entirely out of credit reads “credit” and was never asked for.
        Every invoice is the same document the customer can download from his own meter, built from
        the same figures, and it carries no VAT because nothing here knows any.
      </p>
    </>
  );
}

// ---------------------------------------------------------------- settings & actions

function SettingsTab({ detail }: { detail: Detail }) {
  const client = useQueryClient();
  const identity = detail.identity;
  const organisationId = identity.organisation_id;
  const [pending, setPending] = useState<AccountStatus | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['customer', organisationId] });
    void client.invalidateQueries({ queryKey: ['customers'] });
    void client.invalidateQueries({ queryKey: ['overview'] });
    void client.invalidateQueries({ queryKey: ['activity', organisationId] });
  };

  const setStatus = useMutation({
    mutationFn: (status: AccountStatus) =>
      request<StatusChanged>(`/api/v1/admin/customers/${organisationId}/status`, {
        method: 'POST',
        body: { status, reason: reason.trim() || null },
      }),
    onSuccess: (result) => {
      setPending(null);
      setReason('');
      setDone(
        result.sessions_forgotten
          ? `Account is now ${result.account_status}. ${result.sessions_forgotten} session${result.sessions_forgotten === 1 ? '' : 's'} signed out.`
          : `Account is now ${result.account_status}.`,
      );
      refresh();
    },
  });

  const setFeature = useMutation({
    mutationFn: ({ feature, enabled }: { feature: string; enabled: boolean }) =>
      request<ActionDone>(`/api/v1/admin/customers/${organisationId}/features/${feature}`, {
        method: 'PUT',
        body: { enabled },
      }),
    onSuccess: (result) => {
      setDone(result.detail);
      refresh();
    },
  });

  const status = identity.account_status;
  const canSuspend = status === 'active' || status === 'trialing';
  const features = ['field_notes', 'night_shift', 'business_understanding'];

  return (
    <>
      {done ? (
        <div className="notice good" role="status">
          {done}
        </div>
      ) : null}

      <div className="panel-grid">
        <Panel eyebrow="Standing" title="Account status">
          <p className="muted" style={{ marginTop: 0 }}>
            Suspending signs every member out at once and refuses them at the door until
            reactivated. Closing does the same and is permanent from here. Nothing is ever deleted:
            the rows, the bills and the history stay.
          </p>
          <div className="row">
            <Pill value={status} />
            {canSuspend ? (
              <button type="button" className="button warn" onClick={() => setPending('suspended')}>
                Suspend
              </button>
            ) : null}
            {status === 'suspended' ? (
              <button type="button" className="button" onClick={() => setPending('active')}>
                Reactivate
              </button>
            ) : null}
            {status === 'trialing' ? (
              <button type="button" className="button-quiet" onClick={() => setPending('active')}>
                Convert to active
              </button>
            ) : null}
            {status !== 'closed' ? (
              <button
                type="button"
                className="button-quiet danger"
                onClick={() => setPending('closed')}
              >
                Close account
              </button>
            ) : (
              <button type="button" className="button-quiet" onClick={() => setPending('active')}>
                Reopen
              </button>
            )}
          </div>
          {setStatus.error && !pending ? (
            <Failed error={setStatus.error} fallback="Could not change the status." />
          ) : null}
        </Panel>

        <Panel eyebrow="Features" title="Switches">
          <div className="stack">
            {features.map((feature) => {
              const on = identity.feature_flags[feature] ?? false;
              const explicit = feature in identity.feature_flags;
              return (
                <label key={feature} className="switch">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={setFeature.isPending}
                    onChange={(event) =>
                      setFeature.mutate({ feature, enabled: event.target.checked })
                    }
                  />
                  <span>{feature.replace(/_/g, ' ')}</span>
                  {!explicit ? <span className="faint small">default</span> : null}
                </label>
              );
            })}
          </div>
          {setFeature.error ? (
            <Failed error={setFeature.error} fallback="Could not change the feature." />
          ) : null}
        </Panel>
      </div>

      <IdentityForm detail={detail} onSaved={(message) => (setDone(message), refresh())} />

      {pending ? (
        <Confirm
          title={
            pending === 'closed'
              ? `Close ${identity.name}?`
              : pending === 'suspended'
                ? `Suspend ${identity.name}?`
                : `Set ${identity.name} to ${pending}?`
          }
          body={
            <>
              <p style={{ margin: 0 }}>
                {pending === 'closed'
                  ? `Every member is signed out and refused from now on. The account keeps its history and its bills but cannot be used. Reopening is possible from this screen.`
                  : pending === 'suspended'
                    ? `Every member is signed out now and told the account is suspended until you reactivate it. Use this when a bill is unpaid.`
                    : `Members can sign in again immediately.`}
              </p>
              <label className="field" style={{ margin: 0 }}>
                <span>Reason (goes in the audit trail)</span>
                <input
                  type="text"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  maxLength={300}
                  placeholder="Two invoices unpaid"
                />
              </label>
              {setStatus.error ? (
                <Failed error={setStatus.error} fallback="Could not change the status." />
              ) : null}
            </>
          }
          {...(pending === 'closed' ? { word: 'CLOSE', danger: true } : {})}
          verb={
            pending === 'closed' ? 'Close account' : pending === 'suspended' ? 'Suspend' : 'Confirm'
          }
          busy={setStatus.isPending}
          onConfirm={() => setStatus.mutate(pending)}
          onClose={() => setPending(null)}
        />
      ) : null}
    </>
  );
}

function IdentityForm({ detail, onSaved }: { detail: Detail; onSaved: (message: string) => void }) {
  const identity = detail.identity;
  const [form, setForm] = useState({
    organisation_name: identity.name,
    display_name: identity.display_name,
    trading_description: identity.trading_description,
    owner_first_name: identity.owner_first_name ?? '',
    reply_number: identity.reply_number ?? '',
    sms_sender_id: identity.sms_sender_id ?? '',
    sms_sender_approved: identity.sms_sender_approved,
    billing_reference_prefix: identity.billing_reference_prefix,
    microsoft_tenant_id: identity.microsoft_tenant_id,
  });

  const save = useMutation({
    mutationFn: (body: UpdateIdentityRequest) =>
      request<ActionDone>(`/api/v1/admin/customers/${identity.organisation_id}/identity`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (result) => onSaved(result.detail),
  });

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    // Only what changed goes over the wire. An untouched field is "leave it alone"; an optional
    // field emptied is "clear it", which the API reads from the empty string.
    const body: UpdateIdentityRequest = {};
    if (form.organisation_name !== identity.name) body.organisation_name = form.organisation_name;
    if (form.display_name !== identity.display_name) body.display_name = form.display_name;
    if (form.trading_description !== identity.trading_description)
      body.trading_description = form.trading_description;
    if (form.owner_first_name !== (identity.owner_first_name ?? ''))
      body.owner_first_name = form.owner_first_name;
    if (form.reply_number !== (identity.reply_number ?? '')) body.reply_number = form.reply_number;
    if (form.sms_sender_id !== (identity.sms_sender_id ?? ''))
      body.sms_sender_id = form.sms_sender_id;
    if (form.sms_sender_approved !== identity.sms_sender_approved)
      body.sms_sender_approved = form.sms_sender_approved;
    if (form.billing_reference_prefix !== identity.billing_reference_prefix)
      body.billing_reference_prefix = form.billing_reference_prefix;
    if (form.microsoft_tenant_id !== identity.microsoft_tenant_id)
      body.microsoft_tenant_id = form.microsoft_tenant_id;
    save.mutate(body);
  }

  return (
    <Panel eyebrow="Identity" title="How they introduce themselves">
      <form onSubmit={submit}>
        {save.error ? <Failed error={save.error} fallback="Could not save." /> : null}
        <div className="field-grid">
          <label className="field">
            <span>Organisation</span>
            <input
              type="text"
              value={form.organisation_name}
              onChange={(e) => set('organisation_name', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Trading name</span>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => set('display_name', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Trading description</span>
            <input
              type="text"
              value={form.trading_description}
              onChange={(e) => set('trading_description', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Owner first name</span>
            <input
              type="text"
              value={form.owner_first_name}
              onChange={(e) => set('owner_first_name', e.target.value)}
            />
          </label>
          <label className="field">
            <span>Reply number (E.164)</span>
            <input
              type="text"
              value={form.reply_number}
              onChange={(e) => set('reply_number', e.target.value)}
              placeholder="+447700900123"
            />
          </label>
          <label className="field">
            <span>SMS sender ID</span>
            <input
              type="text"
              value={form.sms_sender_id}
              onChange={(e) => set('sms_sender_id', e.target.value)}
              maxLength={11}
            />
          </label>
          <label className="field">
            <span>Billing reference prefix</span>
            <input
              type="text"
              value={form.billing_reference_prefix}
              onChange={(e) => set('billing_reference_prefix', e.target.value)}
              maxLength={8}
            />
          </label>
          <label className="field">
            <span>Microsoft tenant</span>
            <input
              type="text"
              value={form.microsoft_tenant_id}
              onChange={(e) => set('microsoft_tenant_id', e.target.value)}
            />
          </label>
        </div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <label className="switch">
            <input
              type="checkbox"
              checked={form.sms_sender_approved}
              onChange={(e) => set('sms_sender_approved', e.target.checked)}
            />
            <span>Sender ID approved by Twilio</span>
          </label>
          <button type="submit" className="button" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save identity'}
          </button>
        </div>
      </form>
    </Panel>
  );
}
